import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { type LiveEvent, subscribeToSpaceLiveEvents } from "../../../shared/api/live";
import { useTRPC } from "../../../shared/api/trpc";

export function useSpaceLiveEvents({
  onBoardViewOrderChanged,
  onBoardUpdated,
  onBoardItemGroupChanged,
  onFolderUpdated,
  onSpaceUpdated,
  onTreeInvalidated,
  onTreeReordered,
  spaceId,
}: {
  onBoardViewOrderChanged: (
    input: Extract<LiveEvent, { topic: "board.view.order.changed" }>["payload"] & { boardViewId: string },
  ) => void;
  onBoardUpdated: (input: { boardId: string; name: string }) => void;
  onBoardItemGroupChanged: (
    input: Extract<LiveEvent, { topic: "board.item.group.changed" }>["payload"] & { boardItemId: string },
  ) => void;
  onFolderUpdated: (input: { folderId: string; name: string }) => void;
  onSpaceUpdated: (input: { name: string; spaceId: string }) => void;
  onTreeInvalidated: (event: Extract<LiveEvent, { topic: "tree.changed" }>) => void;
  onTreeReordered: (event: Extract<LiveEvent, { topic: "tree.reordered" }>) => void;
  spaceId: string;
}) {
  const handlersRef = useRef({
    onBoardItemGroupChanged,
    onBoardUpdated,
    onBoardViewOrderChanged,
    onFolderUpdated,
    onSpaceUpdated,
    onTreeInvalidated,
    onTreeReordered,
  });
  handlersRef.current = {
    onBoardItemGroupChanged,
    onBoardUpdated,
    onBoardViewOrderChanged,
    onFolderUpdated,
    onSpaceUpdated,
    onTreeInvalidated,
    onTreeReordered,
  };

  const queryClient = useQueryClient();
  const trpc = useTRPC();

  useEffect(() => {
    if (!spaceId) {
      return;
    }

    const controller = new AbortController();
    subscribeToSpaceLiveEvents({
      onEvent(event) {
        const handlers = handlersRef.current;
        if (event.topic === "sync.reset") {
          // The server pruned events this client never saw — targeted patches
          // are impossible, every server-derived cache may be stale.
          void queryClient.invalidateQueries();
        }
        if (event.topic === "tree.changed") {
          handlers.onTreeInvalidated(event);
        }
        if (event.topic === "tree.reordered") {
          handlers.onTreeReordered(event);
        }
        if (event.topic === "board.view.order.changed") {
          handlers.onBoardViewOrderChanged({ ...event.payload, boardViewId: event.entityId });
        }
        if (event.topic === "space.updated") {
          handlers.onSpaceUpdated({ name: event.payload.name, spaceId: event.entityId });
        }
        if (event.topic === "folder.updated") {
          handlers.onFolderUpdated({ folderId: event.entityId, name: event.payload.name });
        }
        if (event.topic === "board.updated") {
          handlers.onBoardUpdated({ boardId: event.entityId, name: event.payload.name });
        }
        if (event.topic === "board.item.group.changed") {
          handlers.onBoardItemGroupChanged({ ...event.payload, boardItemId: event.entityId });
        }
        if (event.topic === "workObject.comments.changed") {
          void queryClient.invalidateQueries({
            queryKey: trpc.workObjectComments.list.queryKey({ workObjectId: event.payload.workObjectId }),
          });
        }
        if (event.topic === "sprint.changed") {
          void queryClient.invalidateQueries({
            queryKey: trpc.sprints.list.queryKey({ boardId: event.payload.boardId }),
          });
        }
        if (event.topic === "document.changed") {
          // Document queries are keyed by anchor (work object / view), not by
          // document id — refresh them all; they are few and cheap.
          void queryClient.invalidateQueries({ queryKey: trpc.documents.forWorkObject.queryKey() });
          void queryClient.invalidateQueries({ queryKey: trpc.documents.forView.queryKey() });
        }
      },
      signal: controller.signal,
      spaceId,
    });
    return () => controller.abort();
  }, [queryClient, spaceId, trpc]);
}
